import { useState, type ComponentType, type RefObject } from 'react'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { MoreFields, PrimarySelects, ReviewChip, type Patch, type ReviewChipSlotProps } from './filterControls'
import type { TxnFilters } from './filters'
import type { FilterOptions } from './useFilterOptions'

type Props = {
  open: boolean
  onClose: () => void
  filters: TxnFilters
  options: FilterOptions
  reviewCount: number | undefined
  /**
   * A component, not a hook: it renders the Needs review chip for the month
   * being drafted, fetching that month's count itself. The sheet stays
   * presentational. Absent, only the applied month has a known count.
   */
  ReviewChipFor?: ComponentType<ReviewChipSlotProps>
  today: Date
  /** The draft, committed as one write. */
  onApply: (next: TxnFilters) => void
  /** Clears every filter and the search, at once. */
  onClearAll: () => void
  restoreFocusTo: RefObject<HTMLElement | null>
}

/**
 * The phone's filter sheet: full-screen, the same controls as the bar, with
 * **Apply** and **Clear all** docked at the bottom.
 *
 * It edits a DRAFT. Nothing reaches the URL until Apply, so changing three
 * filters is one request rather than three, and Close discards the lot. The
 * draft is seeded each time the sheet opens, so a filter set elsewhere is
 * never shown stale.
 */
export function FilterSheet(props: Props) {
  // Mounted only while open, so the draft is re-seeded from the current
  // filters on every open without an effect to do it.
  if (!props.open) return null
  return <OpenSheet {...props} />
}

function OpenSheet({ onClose, filters, options, reviewCount, ReviewChipFor, today, onApply, onClearAll, restoreFocusTo }: Props) {
  const [draft, setDraft] = useState(filters)
  const patch = (p: Patch) => setDraft((d) => ({ ...d, ...p }))
  const toggleReview = () => patch({ review: !draft.review })
  return (
    <Overlay open onClose={onClose} label="Filters" placement="sheet" restoreFocusTo={restoreFocusTo}>
      <div className="txn-sheet">
        <h3 className="txn-sheet__title">Filters</h3>
        <div className="txn-sheet__body">
          <PrimarySelects filters={draft} options={options} today={today} onPatch={patch} />
          <MoreFields filters={draft} options={options} onPatch={patch} />
          {/* The count belongs to the month being drafted, not the one applied. */}
          {ReviewChipFor ? (
            <ReviewChipFor month={draft.month} pressed={draft.review} onToggle={toggleReview} />
          ) : (
            <ReviewChip
              pressed={draft.review}
              count={draft.month === filters.month ? reviewCount : undefined}
              onToggle={toggleReview}
            />
          )}
        </div>
        <div className="txn-sheet__actions">
          <Button type="button" size="panel" variant="outline" onClick={onClearAll}>
            Clear all
          </Button>
          <Button type="button" size="panel" variant="primary" onClick={() => onApply(draft)}>
            Apply
          </Button>
        </div>
      </div>
    </Overlay>
  )
}
